// @flow
import React from 'react'
import { connect } from 'react-redux'
import I18n from 'i18n-react'
import Modal from 'reactstrap/lib/Modal'
import ModalBody from 'reactstrap/lib/ModalBody'
import GuardXLogger from 'guard-x-logger'
import moment from '../Core/Moment'
import { assocPath } from 'ramda'

// redux
import ProductActions from '../Core/Redux/ProductRedux'
import UserDataActions from '../Core/Redux/UserDataRedux'
import ValidationActions from '../Core/Redux/ValidationRedux'
import QuoteActions, { INITIAL_STATE } from '../Core/Redux/QuoteRedux'
import NavigationActions from '../Core/Redux/NavigationRedux'
import FilterActions from '../Core/Redux/FilterRedux'
import GeneralActions from '../Core/Redux/GeneralRedux'
import LinkBuilderActions from '../Core/Redux/LinkBuilderRedux'
import DiscountsActions from '../Core/Redux/DiscountsRedux'
import GTMActions from '../Core/Redux/GTMRedux'
import LeavingScreenActions from '../redux/LeavingScreenRedux'
import { WEB_TYPES_KEYS } from '../Core/Redux/SettingsRedux'

// core
import FieldValidator from '../Core/FieldValidator'
import { onValidationErrorsChange } from '../Core/ContainerFunctions'
import { getValueForPath } from '../Core/FilterFunctions'

// selectors
import { selectAdjustedValidationErrors, selectProductDescriptorValues } from '../selectors/validation'
import { selectActiveSettings, showCebiaSelector } from '../selectors/settings'
import { selectAllValueNames, selectArrayTypes } from '../selectors/product'
import { selectPatchedEnabledCalculators } from '../selectors/filter'
import { selectIsAppRehydrated } from '../selectors/reduxPersist'

// serializers
import { quoteAsyncRequestSerializer } from '../Core/Serializers/QuoteAsyncRequestSerializer'

// config
import { getFirstElement } from '../functions/error'

// config
import { config } from '../Config/ConfigLoader'

// components
import VehicleInfoApp from '../Components/_miniApps/VehicleInfoApp'
import { FieldForm, ErrorsPanel } from '../Components/'
import { VariablesDebugBlock, ActionDebugRow } from '../Components/_debug/'
import CallbackFormSubmodule from '../Components/_miniApps/CallbackFormApp'

// types
import type { PathArrayType, ProductContent } from '../Core/Types/ProductContent'
import type { Match } from '../Core/Types/Navigation'
import type { ExpandedSpecificationDTO } from '../Core/Types/Field'
import type { ValidationErrorType, ValidationErrorsType } from '../Core/Types/ValidationError'
import type { InputHelpsType } from '../Core/Types/InputHelpType'
import type { ArrayFieldFormTypes } from '../Core/Types/ArrayFieldFormType'
import type { ActiveSettingsType } from '../Core/Types/SettingsTypes'
import type { LocationType } from '../Core/Types/LocationType'
import type { TranslationType } from '../Core/Types/TranslationTypes'
import type { ResourcesType } from '../Core/Types/Resources'
import type { FormValueType, ValueObject } from '../Core/Types/Values'
import type { AsMutable } from '../Core/Types/ImmutableTypes'
import type { InsuranceCompaniesType } from '../Core/Types/InsuranceCompanyTypes'
import type { QuoteIdDependsOnType } from '../Core/Redux/Types/QuoteStateType'
import type { QuoteAsyncRequestSerializerType } from '../Core/Serializers/QuoteAsyncRequestSerializer'
import type { QuoteFilterBlockType } from '../Core/Types/FilterTypes'

type Props = {
  activeQuoteFilters: QuoteFilterBlockType,
  activeSettings: ActiveSettingsType & AsMutable<ActiveSettingsType>,
  addGtmEvent: Function,
  affiliate: string,
  arrayTypes: ArrayFieldFormTypes,
  beforeHydration: Object,
  debug: boolean,
  defaultTemplateVariables: AsMutable<ValueObject>,
  enabledCalculators: Array<string>,
  goToTab: string,
  insuranceCompanies: InsuranceCompaniesType,
  isBackOffice: boolean,
  linkSaveRequestSig: boolean,
  location: LocationType,
  match: Match,
  onActionFailure: (error: Object, errorType: number) => void,
  onChangeActiveTab: (string) => void,
  onChangeData: (formData: Object) => void,
  onChangeGeneralFailureError: (error: Error, key: string, data: any) => void,
  onChangeProductSpecification: (ExpandedSpecificationDTO) => void,
  onChangeShowValidationErrorPanel: (show: boolean) => void,
  onChangeValidationError: (key: string | PathArrayType, data: Array<string> | boolean) => void,
  onChangeValidationErrors: (validationErrors: ValidationErrorsType) => void,
  onChangeValidationSig: (validationSig: boolean) => void,
  onResetValidationErrors: () => void,
  onSetLastValidStep: (key: string, pathname: string) => void,
  onSetLastValidStepIndex: (string) => void,
  previousRequestId: string,
  productContent: ProductContent,
  productContentRequest: (product: string) => void,
  productInputHelps: InputHelpsType,
  productName: string,
  productResourcesRequest: (product: string) => void,
  productTexts: TranslationType,
  quoteAsyncRequest: (string, QuoteAsyncRequestSerializerType, string, ?QuoteIdDependsOnType, string) => void,
  quoteFilters: QuoteFilterBlockType,
  rehydrated: boolean,
  requestLinkId: string,
  resetFilterData: () => void,
  resetFilterFunctionalData: () => void,
  resetQuotes: (boolean) => void,
  resources: ResourcesType,
  saveUtmParams: () => void,
  sendLinkRequestLoaderOff: (string, Object, link?: string) => void,
  setEnabledCalculators: (Array<string>) => void,
  setFilter: (tab: string, name: string, value: string, multiSelect?: boolean) => void,
  setGtmPageView: (string, string) => void,
  setLinkSaveRequestSig: (boolean) => void,
  setNumberOfItemsInArrayType: (string, string, number) => void,
  setPayment: (string) => void,
  setProductLinkTitle: (string) => void,
  setProductLinkUrlRequest: Function, // TODO
  setProperties: (Object) => null,
  setQuoteFilters: (any) => void, // TODO
  setQuoteRequestDataCalculators: (Array<string>, Array<string> | false) => void,
  setStompConnected: (boolean) => void,
  showValidationErrorPanel: boolean,
  specification: ExpandedSpecificationDTO,
  subProductName: string,
  token: string,
  updateArrayTypes: (ExpandedSpecificationDTO) => void,
  userData: { [key: string]: any },
  userDataInit: (specification: ExpandedSpecificationDTO) => void,
  userDataRemoveValue: (key: string) => void,
  userDataSetValue: (key: string | PathArrayType, value: FormValueType) => void,
  validationErrors: ValidationErrorType,
  validationSig: boolean,
  validator: FieldValidator,
  valueNames: { [key: string]: { [key: string]: string } },
  wasCicCompleted: (boolean) => void,
  webType: number,
}

type State = {
  debugModalOpened: boolean,
  pushIsPristine: boolean,
  sendCallbackOnInactive: boolean,
  showCallback: boolean,
}

class ProductDetailContainer extends React.Component<Props, State> {
  _callBackTimer: number

  state = {
    debugModalOpened: false,
    pushIsPristine: true,
    sendCallbackOnInactive: false,
    showCallback: false,
  }

  componentDidMount() {
    const { isBackOffice, userData, location } = this.props
    const { initProduct } = this.props
    const { sendCallbackOnInactive } = this.state

    initProduct(location.pathname)

    if (sendCallbackOnInactive) {
      this.setState({ sendCallbackOnInactive: false, showCallback: false })
    }

    if (!isBackOffice || !userData.getIn(['paymentPeriod'], false)) {
      this.handleOnChangePayment('annually')
    }

    if (this.props.rehydrated) {
      this.handleConnectLeavingScreenCl()
      this.handleCheckData()
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { validationSig, userData, rehydrated } = this.props
    const { onChangeValidationSig } = this.props
    if (validationSig) {
      onChangeValidationSig(false)
      this.handleValidate()
    }
    if (rehydrated !== prevProps.rehydrated) {
      this.handleConnectLeavingScreenCl()
      this.handleCheckData()
      if (this.props.beforeHydration && Object.keys(this.props.beforeHydration).length) {
        this.handleChangeFormData(this.props.beforeHydration, [])
      } else {
        this.handleValidate()
      }
    }
    if (!userData.getIn(['paymentPeriod'], false)) {
      this.handleOnChangePayment('annually')
    }
  }

  componentWillUnmount() {
    if (this._callBackTimer) {
      clearTimeout(this._callBackTimer)
    }
    window.sendLeavingScreenLog = undefined
  }

  handleConnectLeavingScreenCl = () => {
    window.sendLeavingScreenLog = () => {
      this.props.sendLeaveScreenLog()
    }
  }

  handleCheckData() {
    const { userData } = this.props
    let userDataNew = userData
    const insuranceStart = userData.getIn(['insuranceStart'], false)
    if (insuranceStart) {
      if (moment().isSameOrAfter(moment(insuranceStart), 'second')) {
        userDataNew = userDataNew.setIn(
          ['insuranceStart'],
          moment().endOf('day').subtract(5, 'minutes').format('YYYY-MM-DDTHH:mm'),
        )
      }
    }
    if (userData.getIn(['elCommunicationAgreed'], false)) {
      userDataNew = userDataNew.set('elCommunicationAgreed', false)
    }
    userDataNew = userDataNew.setIn(['policyHolder', 'email'], '')
    if (userDataNew !== userData) {
      this.handleChangeFormData(userDataNew, [])
    }
  }

  handleChangeValue = (key: string, value: FormValueType, pathArray: PathArrayType) => {
    const {
      onChangeValidationError,
      leavingScreenMergeParameters,
      userDataSetValue,
      onSetLastValidStepIndex,
      onChangeData,
      userData,
    } = this.props
    leavingScreenMergeParameters({ lastInputFocus: pathArray })
    const emptyVehicle = {
      model: '',
      category: '',
      capacity: '',
      power: '',
      weight: '',
      firstRegistered: '',
      fuel: '',
      seats: '',
      yearOfConstruction: '',
      vin: '',
      regPlate: '',
    }

    switch (key) {
      case 'type':
        onChangeData({ ...emptyVehicle, manufacturer: '' })
        break
      case 'manufacturer':
        if (value !== userData.manufacturer) {
          onChangeData(emptyVehicle)
        }
        break
      case 'noPrivateOffers':
        onChangeValidationError('policyHolder', false)
    }
    userDataSetValue(pathArray, value)
    onSetLastValidStepIndex('product')
    const { errors } = this.handleValidate(pathArray, value)
    const error = getValueForPath(pathArray, 0, errors)
    onChangeValidationError(pathArray, error ? error : false)
  }

  handleChangeFormData = (formData: Object, loaded = false) => {
    const { onChangeData, onSetLastValidStepIndex, onChangeValidationSig } = this.props
    onChangeData(formData, loaded)
    onSetLastValidStepIndex('product')
    onChangeValidationSig(true)
  }

  handleValidate = (pathArray?: PathArrayType, value?: FormValueType, pushSaveErrors: boolean = false) => {
    const {
      userData,
      onChangeValidationErrors,
      productDescriptorValues,
      onChangeProductSpecification,
      validate,
    } = this.props
    if (productDescriptorValues && typeof validate === 'function') {
      // todo corner case - setting directly to product descriptor values
      const customProductDescriptorValues =
        Array.isArray(pathArray) && typeof value !== 'undefined'
          ? assocPath(pathArray, value, productDescriptorValues)
          : productDescriptorValues

      const { isValid, errors, specification } = validate(customProductDescriptorValues, userData)
      onChangeProductSpecification(specification)

      if (pushSaveErrors) {
        onChangeValidationErrors(onValidationErrorsChange(errors, userData))
      }

      return { isValid, errors }
    }
    return { isValid: false, errors: {} }
  }

  handleOnChangePayment = (payment: string) => {
    this.props.setPayment(payment)
  }

  handleChangeValidationErrorFromMiniApp = (key: string | PathArrayType, data: Array<string> | boolean) => {
    this.props.onChangeValidationError(key, data)
    this.props.onChangeShowValidationErrorPanel(true)
  }

  handleSubmit = () => {
    const {
      addGtmEvent,
      discounts,
      enabledCalculators,
      isBackOffice,
      leavingScreenMergeParameters,
      onChangeActiveTab,
      onChangeData,
      onChangeShowValidationErrorPanel,
      onChangeValidationErrors,
      onResetValidationErrors,
      productName,
      quoteAsyncRequest,
      resetFilterData,
      resetQuotes,
      saveUtmParams,
      setQuoteRequestDataCalculators,
      setStompConnected,
      specification,
      subProductName,
      wasCicCompleted,
    } = this.props
    let { productDescriptorValues } = this.props
    let { userData } = this.props

    onResetValidationErrors()
    saveUtmParams()
    leavingScreenMergeParameters({ productStepSubmitted: true })

    if (specification) {
      this.setState({ pushIsPristine: false })

      // todo add to selector
      if (
        moment().isSameOrAfter(moment(productDescriptorValues.insuranceStart), 'second') ||
        productDescriptorValues.insuranceStart === undefined
      ) {
        productDescriptorValues = {
          ...productDescriptorValues,
          insuranceStart: moment().endOf('day').format('YYYY-MM-DDTHH:mm'),
        }
        onChangeData(productDescriptorValues)
      }

      let { isValid, errors } = this.handleValidate()
      // todo ??
      // if (!validator.isValid) {
      //   productDescriptorValues = autofixErrors(validator.errors, productDescriptorValues, specification, defaultValues)
      //   validator = this.handleValidate(undefined, undefined, productDescriptorValues)
      //   if (validator.isValid) {
      //     this.props.onChangeData(productDescriptorValues)
      //   }
      // }

      if (errors.paymentPeriod) {
        GuardXLogger.captureMessage('POV+HAV - paymentPeriod K1', {
          extra: {
            userData,
            specification,
          },
        })
      }

      if (isValid) {
        if (userData.bonusType) {
          addGtmEvent('Fill', 'Bonus')
          switch (userData.bonusType) {
            case 'manually':
              addGtmEvent('Fill', 'Znám počet měsíců')
              break
            case 'notSure':
              addGtmEvent('Fill', 'Nejsem si jist')
              break
          }
        }
        this.handleHideErrorPanel()
        resetQuotes(isBackOffice)
        wasCicCompleted(!!productDescriptorValues.cic)

        let goToTab = config.getConfig('productDetailContainer.quotesKey')
        if (this.props.goToTab && config.getConfig('subProductNames').includes(this.props.goToTab)) {
          goToTab = this.props.goToTab
        } else if (userData.cicEnabled && subProductName === 'cic' && !isBackOffice) {
          goToTab = 'cic'
        } else if (goToTab === 'tplCic' && !userData.cicEnabled) {
          goToTab = 'tpl'
        }

        const quoteRequestData = INITIAL_STATE.getIn(['quoteRequests', goToTab, 'quoteRequestData'], {})
        // https://renomia.atlassian.net/browse/PG-965
        const useFilters = true
        const pushFilters = {
          cic: { type: ['allRisk', 'theft', 'accident'] },
        }

        if (
          (userData.getIn(['type'], '') === 'truck' && userData.getIn(['weight'], 0) > 6000) ||
          (userData.getIn(['type'], '') === 'trailer' && userData.getIn(['subtype'], '') === 'semi-trailer')
        ) {
          pushFilters.cic.participation = ['high']
        }
        const enabledCalculatorsWithoutSuri = enabledCalculators.filter((calculator) => calculator !== 'CarSuriAuto') // PG-3016
        setQuoteRequestDataCalculators(enabledCalculatorsWithoutSuri, false)
        resetFilterData()
        onChangeActiveTab(goToTab)
        setStompConnected(false)
        const asyncData = quoteAsyncRequestSerializer(
          this.props,
          productDescriptorValues,
          quoteRequestData,
          discounts, // todo - poznamka
          'quoteAsyncRequestSerializer',
          enabledCalculatorsWithoutSuri,
          useFilters,
          pushFilters,
        )
        leavingScreenMergeParameters({ productStepCompleted: true })
        if (!productDescriptorValues.noPrivateOffers) {
          addGtmEvent('Fill', 'Neveřejné nabídky')
        }

        quoteAsyncRequest(productName, asyncData, goToTab, undefined, subProductName)
      } else {
        let offsetY = 0
        const firstElement = getFirstElement(errors, offsetY, '')
        if (firstElement) {
          location.replace(`#${firstElement}`)
        }
        onChangeShowValidationErrorPanel(true)
        onChangeValidationErrors(onValidationErrorsChange(errors, userData))
      }
    }
  }

  handleSetNumberOfItemsInArrayType = (key: string, path: string, value: number) => {
    this.props.setNumberOfItemsInArrayType(key, path, value)
  }

  handleToggleDebugModal = () => {
    this.setState({ debugModalOpened: !this.state.debugModalOpened })
  }

  handleHideErrorPanel = () => {
    this.props.onChangeShowValidationErrorPanel(false)
  }

  handleSetCallbackTimer = () => {
    this.setState({ showCallback: true })
    this._callBackTimer = setTimeout(() => {
      this.setState({ sendCallbackOnInactive: true })
    }, 900000)
  }

  handleOnSetDiscounts = (discounts: { [key: string]: any }) => {
    const { setDiscounts } = this.props
    setDiscounts(discounts)
  }

  render() {
    const {
      activeSettings,
      addGtmEvent,
      arrayTypes,
      debug,
      defaultTemplateVariables,
      discounts,
      enabledCalculators,
      insuranceCompanies,
      isBackOffice,
      previousRequestId,
      productContent,
      productInputHelps,
      productName,
      productTexts,
      resources,
      showValidationErrorPanel,
      specification,
      token,
      userData,
      loadedData,
      validationErrors,
      valueNames,
      webType,
      showCebiaForm,
    } = this.props

    // todo
    const templateVariables = {
      ...defaultTemplateVariables,
      ...activeSettings,
      email: I18n.translate('acceptScreen.contactEmail', { context: webType }),
      phoneNumber: I18n.translate('acceptScreen.phoneNumber', { context: webType }),
    }

    const miniApps: { [string]: string | string[] } = {
      'cic-cicVehicleSecurity': ['equipmentMiniApp', 'equipmentPillowMiniApp'],
      usage: 'cicEnableApp',
      user: [],
    }
    // miniApps['cic-priceExcludesVAT'] = 'vehiclePriceApp'
    if (userData.userSameAsPolicyHolder) {
      miniApps['userSameAsPolicyHolder'] = ['bonusMalusApp', 'phoneApp']
    } else {
      miniApps['user'] = ['bonusMalusApp', 'phoneApp']
    }
    miniApps['user'].push('discountsMiniApp')

    return (
      <div className="container container-xxl">
        <ActionDebugRow
          active={debug}
          buttonActions={[this.handleToggleDebugModal]}
          buttonTexts={['Zobrazit dostupné proměnné v šablonách']}
        />
        {productContent && productContent.fieldOrder && specification && specification.fields && (
          <div>
            {showCebiaForm && (
              <div>
                <VehicleInfoApp
                  activeSettings={activeSettings}
                  autoFinish
                  data={userData}
                  formData={userData}
                  isBackOffice={false}
                  miniAppData={{
                    addGtmEvent,
                  }}
                  onChangeFormData={this.handleChangeFormData}
                  resources={resources}
                  token={token}
                  validationErrors={validationErrors}
                />
              </div>
            )}
            <div id="calculator">
              <div className="card card-lg mx-auto bg-transparent border-0 border-sm-default mb-5">
                <div className="card-body px-0 px-sm-3">
                  <div className="text-center">
                    <h2>{I18n.translate('general.infoAboutVehicle')}</h2>
                  </div>
                  <hr className="border-0 border-sm-top border-active-object" />
                  <FieldForm
                    activeSettings={activeSettings}
                    arrayTypes={arrayTypes}
                    blockClassName=""
                    blockContentClassName="px-0 py-sm-2"
                    content={productContent} // todo check if content is OK
                    data={userData}
                    debug={debug}
                    formData={userData}
                    inputHelps={productInputHelps}
                    isBackOffice={isBackOffice}
                    key="form_root"
                    loadedData={loadedData}
                    miniAppData={{
                      addGtmEvent,
                      discounts,
                      insuranceCompanies: insuranceCompanies,
                      ownerRadioApp: {
                        inputHelps: {
                          ownerSameAsPolicyHolder: productInputHelps.ownerSameAsPolicyHolder,
                        },
                        ownerRadioAppTextMode: false,
                      },
                      inputHelps: {
                        drive4x4: productInputHelps.drive4x4,
                        parkingAssistant: productInputHelps.parkingAssistant,
                        automaticTransmission: productInputHelps.automaticTransmission,
                      },
                      phoneAppABInputHelp: productInputHelps.phoneApp,
                      setCallbackTimer: this.handleSetCallbackTimer,
                      setProperties: this.props.setProperties,
                      translationsLoaded: I18n.texts && I18n.texts.translationLoaded,
                      onChangeValidationError: this.handleChangeValidationErrorFromMiniApp,
                      enabledCalculators,
                      setDiscounts: this.handleOnSetDiscounts,
                    }}
                    miniApps={miniApps}
                    onBlur={this.handleOnBlur}
                    onChangeFormData={this.handleChangeFormData}
                    onChangeValue={this.handleChangeValue}
                    pushIsPristine={this.state.pushIsPristine}
                    resources={resources}
                    setNumberOfItemsInArrayType={this.handleSetNumberOfItemsInArrayType}
                    showArrayTypeInput
                    specification={specification}
                    templateVariables={templateVariables}
                    token={token}
                    validationErrors={validationErrors}
                    valueNames={valueNames}
                  />
                </div>
              </div>
              <div className="text-center mt-4 py-2">
                <span>
                  {I18n.translate('general.agreeWithTermsAndConditions', {
                    link: (
                      <a
                        href={`${activeSettings.domain}informace-o-zpracovani-osobnich-udaju/`}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {I18n.translate('general.infoAboutDataProcessing')}
                      </a>
                    ),
                  })}
                </span>
              </div>
              <div className="container">
                <div className="text-center mt-4 py-2 mb-5 ">
                  <button className="btn btn-secondary btn-lg" onClick={this.handleSubmit} type="button">
                    {I18n.translate('submit.step1')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <ErrorsPanel
          debug={debug}
          errors={validationErrors}
          isVisible={showValidationErrorPanel}
          onHide={this.handleHideErrorPanel}
          texts={productTexts}
        />
        {debug && (
          <Modal isOpen={this.state.debugModalOpened} toggle={this.handleToggleDebugModal}>
            <ModalBody>
              <VariablesDebugBlock
                active={debug}
                collapseObjects
                showOnlyTypes
                title="templateVariables"
                variables={templateVariables}
              />
            </ModalBody>
          </Modal>
        )}
        {this.state.showCallback && (
          <div className="d-none">
            <CallbackFormSubmodule
              affiliate=""
              automaticallySend={this.state.sendCallbackOnInactive && !isBackOffice}
              data={{}}
              formData={userData}
              goBack={() => {}}
              isBackOffice={isBackOffice}
              modalOpened={false}
              onClose={() => {}}
              productName={productName}
              renderNotOpened
              requestId={previousRequestId}
              showText={false}
              token={token}
              website={WEB_TYPES_KEYS[webType]}
            />
          </div>
        )}
      </div>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    showCebiaForm: showCebiaSelector(state),
    activeQuoteFilters: state.appData.quote.filters,
    activeSettings: selectActiveSettings(state),
    affiliate: state.appData.product.affiliate,
    arrayTypes: selectArrayTypes(state),
    debug: state.appData.debug.debug,
    defaultTemplateVariables: state.appData.inputHelps.defaultTemplateVariables,
    defaultValues: state.userData.defaultValues,
    enabledCalculators: selectPatchedEnabledCalculators(state),
    goToTab: state.appData.quote.goToTab,
    insuranceCompanies: state.appData.general.insuranceCompanies,
    linkSaveRequestSig: state.appData.linkBuilder.linkSaveRequestSig,
    previousRequestId: state.userData.previousRequestId,
    productContent: state.appData.product.productContent,
    productDescriptorValues: selectProductDescriptorValues(state),
    productInputHelps: state.appData.inputHelps.productInputHelps,
    productName: state.appData.product.productName,
    productTexts: state.appData.product.productTexts,
    quoteFilters: state.appData.product.quoteFilters,
    quoteRequests: state.appData.quote.quoteRequests,
    rehydrated: selectIsAppRehydrated(state),
    requestLinkId: state.appData.linkBuilder.requestLinkId,
    resources: state.appData.product.resources,
    showValidationErrorPanel: state.appData.validation.showValidationErrorPanel,
    specification: state.appData.product.specification,
    subProductName: state.appData.product.subProductName,
    userData: state.userData.data,
    loadedData: state.userData.loadedData,
    validationErrors: selectAdjustedValidationErrors(state),
    validationSig: state.appData.validation.validationSig,
    valueNames: selectAllValueNames(state),
    webType: state.appData.settings.webType,
    discounts: state.appData.discounts.discounts,
    isBackOffice: false,
  }
}

const mapDispatchToProps = {
  addGtmEvent: GTMActions.addGtmEvent,
  initProduct: ProductActions.initProduct,
  leavingScreenMergeParameters: LeavingScreenActions.leavingScreenMergeParameters,
  onActionFailure: GeneralActions.onActionFailure,
  onAddStep: NavigationActions.onAddStep,
  onChangeActiveTab: QuoteActions.onChangeActiveTab,
  onChangeData: UserDataActions.onChangeData,
  onChangeGeneralFailureError: ValidationActions.onChangeGeneralFailureError,
  onChangeProductSpecification: ProductActions.onChangeProductSpecification,
  onChangeShowValidationErrorPanel: ValidationActions.onChangeShowValidationErrorPanel,
  onChangeValidationError: ValidationActions.onChangeValidationError,
  onChangeValidationErrors: ValidationActions.onChangeValidationErrors,
  onChangeValidationSig: ValidationActions.onChangeValidationSig,
  onResetValidationErrors: ValidationActions.onResetValidationErrors,
  onSetLastValidStep: NavigationActions.onSetLastValidStep,
  onSetLastValidStepIndex: NavigationActions.onSetLastValidStepIndex,
  quoteAsyncRequest: QuoteActions.quoteAsyncRequest,
  resetFilterData: FilterActions.resetFilterData,
  resetFilterFunctionalData: FilterActions.resetFilterFunctionalData,
  resetQuotes: QuoteActions.resetQuotes,
  saveGenLinkDataRequest: LinkBuilderActions.saveGenLinkDataRequest,
  sendLeaveScreenLog: LeavingScreenActions.sendLeaveScreenLog,
  sendLinkRequestLoaderOff: LinkBuilderActions.sendLinkRequestLoaderOff,
  setDiscounts: DiscountsActions.setDiscounts,
  setEnabledCalculators: FilterActions.setEnabledCalculators,
  setFilter: QuoteActions.setFilter,
  setGtmPageView: GTMActions.setGtmPageView,
  setLinkSaveRequestSig: LinkBuilderActions.setLinkSaveRequestSig,
  setNumberOfItemsInArrayType: UserDataActions.setNumberOfItemsInArrayType,
  setPayment: UserDataActions.setPayment,
  setProductLinkTitle: LinkBuilderActions.setProductLinkTitle,
  setProductLinkUrlRequest: LinkBuilderActions.setProductLinkUrlRequest,
  setProperties: UserDataActions.setProperties,
  setQuoteFilters: QuoteActions.setQuoteFilters,
  setQuoteRequestDataCalculators: QuoteActions.setQuoteRequestDataCalculators,
  setStompConnected: QuoteActions.setStompConnected,
  updateArrayTypes: UserDataActions.updateArrayTypes,
  userDataInit: UserDataActions.userDataInit,
  userDataRemoveValue: UserDataActions.userDataRemoveValue,
  userDataSetValue: UserDataActions.userDataSetValue,
  userDataSetValueInsuredParties: UserDataActions.userDataSetValueInsuredParties,
  wasCicCompleted: UserDataActions.wasCicCompleted,
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductDetailContainer)
